import * as React from "react";
import { Helmet } from "react-helmet";
import Loadable from "react-loadable";
import { Redirect, useHistory } from "react-router";
import { Route, Switch } from "react-router-dom";

import { ROUTES } from "@config/app.routing";
import { LOADABLE_COMPONENT_DELAY } from "@config/consts";
import { CLIENT_DASHBOARD_TABS } from "@config/testIds/clientDashboard";

import { AVAILABLE_COUNTRIES } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { FadeInContainer } from "@ui/Atoms";
import { Container } from "@ui/Atoms/Grid";
import { HeaderTabsWLinks } from "@ui/Molecules/HeaderTabsWLinks";
import {
  BookingsToValidateDashboardSkeleton,
  MyCleaningsDashboardSkeleton,
  OptionsDashboardSkeleton,
  ProfileDashboardSkeleton,
  BlockPageSkeleton,
} from "@ui/Skeletons/Skeletons";
import {
  ListParagraphDotsCheckmark,
  UserProfileCircle,
} from "@ui/Assets/MyIcons";

import { ScrollToTop } from "@containers/ScrollToTop";

import { useTranslate } from "@hooks/useTranslate";
import {
  useFeatureToggle,
  useFeatureToggleManager,
} from "@hooks/useFeatureToggle";
import { useLocalisation } from "@hooks/useLocalisation";

import { NotFound } from "@app/pages/NotFound";
import { ChecklistRouteProvider } from "@app/pages/Checklist/hooks/useChecklistRoute";

import { ProfileLayout } from "./components";
import { useSetupCleaningChecklistQuery } from "./ClientDashboard.hooks";

interface Props {
  bookingsToValidate?: boolean;
  currentUrl: string;
}

const LoadableMyCleaningsDashboard: any = Loadable({
  loader: () => import("./MyCleaningsDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.MyCleaningsDashboard;
    return <Component />;
  },
  loading: MyCleaningsDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableProfileDashboard: any = Loadable({
  loader: () => import("./ProfileDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.ProfileDashboard;
    return <Component />;
  },
  loading: ProfileDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableDeleteAccount: any = Loadable({
  loader: () => import("./DeleteAccount"),
  render(loaded: any): JSX.Element {
    const Component = loaded.DeleteAccount;
    return <Component />;
  },
  loading: ProfileDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableBookingsToValidate: any = Loadable({
  loader: () => import("./BookingsToValidateDashboard"),
  render(loaded: any): JSX.Element {
    const Component = loaded.BookingsToValidateDashboard;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableRating: any = Loadable({
  loader: () => import("./Rating"),
  render(loaded: any): JSX.Element {
    const Component = loaded.Rating;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableCleaningCancellation: any = Loadable({
  loader: () => import("../CleaningCancellation"),
  render(loaded: any): JSX.Element {
    const Component = loaded.CleaningCancellation;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableContractCancellation: any = Loadable({
  loader: () => import("../ContractCancellation"),
  render(loaded: any): JSX.Element {
    const Component = loaded.ContractCancellation;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableAutomaticReschedule: any = Loadable({
  loader: () => import("../AutomaticReplaces/AutomaticReschedule"),
  render(loaded: any): JSX.Element {
    const Component = loaded.AutomaticReschedule;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableAgentReplacement: any = Loadable({
  loader: () => import("../AutomaticReplaces/AgentReplacement"),
  render(loaded: any): JSX.Element {
    const Component = loaded.AgentReplacement;
    return <Component />;
  },
  loading: BookingsToValidateDashboardSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const LoadableChecklist: any = Loadable({
  loader: () => import("../Checklist"),
  render(loaded: any): JSX.Element {
    const Component = loaded.Checklist;
    return <Component />;
  },
  loading: BlockPageSkeleton,
  delay: LOADABLE_COMPONENT_DELAY,
});

const OptionsToProfileRedirect = () => {
  const history = useHistory();
  React.useEffect(() => {
    const path = history.location.pathname.replace(
      ROUTES.CLIENT_DASHBOARD_OPTIONS,
      ROUTES.CLIENT_DASHBOARD_PROFILE,
    );
    history.push(`${path}${history.location.search}`);
  }, []);

  return null;
};

const ClientDashboardView = (props: Props): React.ReactElement => {
  const translate = useTranslate();
  const { country } = useLocalisation();
  const { status: featureToggleStatus } = useFeatureToggleManager();
  useSetupCleaningChecklistQuery();

  const didFetchFeatureToggles = [
    LOADING_STATE.FAILED,
    LOADING_STATE.SUCCEEDED,
  ].includes(featureToggleStatus);

  const isReschedulingAutomationEnabled = useFeatureToggle(
    "rescheduling_automation",
  );
  const isAgentAutoReplacementEnabled = useFeatureToggle(
    "agent_replacement_automation",
  );

  const paths = {
    rating: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS_RATING}/:uuid`,
    options: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_OPTIONS}`,
    profile: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_PROFILE}`,
    deleteAccount: `${props.currentUrl}${ROUTES.DELETE_ACCOUNT}`,
    bookingsToValidate: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`,
    myCleaningsDashboard: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
    cleaningCancellation: `${props.currentUrl}${ROUTES.JOB_CANCELLATION}`,
    contractCancellation: `${props.currentUrl}${ROUTES.CONTRACT_CANCELLATION}`,
    automaticReschedule: `${props.currentUrl}${ROUTES.AUTOMATIC_RESCHEDULE}`,
    agentReplacement: `${props.currentUrl}${ROUTES.AGENT_AUTO_REPLACED}`,
    checklist: `${props.currentUrl}${ROUTES.CLIENT_CHECKLIST}`,
  };

  const tabs = [
    {
      testId: CLIENT_DASHBOARD_TABS.MY_CLEANINGS,
      to: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
      label: translate("batmaid_pwa_navigation.subnav_my_cleaning"),
      icon: ListParagraphDotsCheckmark,
    },
    ...(props.bookingsToValidate
      ? [
          {
            testId: CLIENT_DASHBOARD_TABS.BOOKINGS_TO_VALIDATE,
            to: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`,
            label: translate(
              "batmaid_pwa_navigation.subnav_bookings_to_validate",
            ),
          },
        ]
      : []),
    {
      testId: CLIENT_DASHBOARD_TABS.PROFILE,
      to: `${props.currentUrl}${ROUTES.CLIENT_DASHBOARD_PROFILE}`,
      label: translate("batmaid_pwa_navigation.subnav_profile"),
      icon: UserProfileCircle,
    },
  ];

  return (
    <React.Fragment>
      <ScrollToTop />
      {country === AVAILABLE_COUNTRIES.CH && (
        <Helmet>
          <title>{translate("batmaid_pwa_my_cleanings.meta_title")}</title>
        </Helmet>
      )}

      <Switch>
        <Route exact path={props.currentUrl}>
          <Container>
            <Redirect to={paths.myCleaningsDashboard} />
          </Container>
        </Route>

        <Route
          exact
          path={paths.myCleaningsDashboard}
          render={() => (
            <ProfileLayout tabs={tabs}>
              <LoadableMyCleaningsDashboard />
            </ProfileLayout>
          )}
        />
        <Route
          exact
          path={paths.bookingsToValidate}
          render={() => (
            <Container>
              <HeaderTabsWLinks tabs={tabs} />
              <FadeInContainer>
                <LoadableBookingsToValidate />
              </FadeInContainer>
            </Container>
          )}
        />
        <Route
          path={paths.profile}
          render={() => (
            <ProfileLayout tabs={tabs}>
              <LoadableProfileDashboard />
            </ProfileLayout>
          )}
        />
        <Route
          exact
          path={paths.deleteAccount}
          render={() => (
            <Container>
              <FadeInContainer>
                <LoadableDeleteAccount />
              </FadeInContainer>
            </Container>
          )}
        />
        <Route
          path={paths.options}
          render={() => <OptionsToProfileRedirect />}
        />
        <Route
          path={paths.rating}
          render={() => (
            <Container>
              <FadeInContainer>
                <LoadableRating />
              </FadeInContainer>
            </Container>
          )}
        />

        <Route
          path={paths.cleaningCancellation}
          render={() => (
            <Container>
              <FadeInContainer>
                <LoadableCleaningCancellation />
              </FadeInContainer>
            </Container>
          )}
        />

        <Route
          exact
          path={paths.contractCancellation}
          render={() => (
            <Container>
              <FadeInContainer>
                <LoadableContractCancellation />
              </FadeInContainer>
            </Container>
          )}
        />

        <Route
          exact
          path={paths.automaticReschedule}
          render={() => (
            <Container>
              <FadeInContainer>
                {didFetchFeatureToggles && (
                  <React.Fragment>
                    {isReschedulingAutomationEnabled ? (
                      <LoadableAutomaticReschedule />
                    ) : (
                      <NotFound isNested />
                    )}
                  </React.Fragment>
                )}
              </FadeInContainer>
            </Container>
          )}
        />

        <Route
          exact
          path={paths.agentReplacement}
          render={() => (
            <Container>
              <FadeInContainer>
                {didFetchFeatureToggles && (
                  <React.Fragment>
                    {isAgentAutoReplacementEnabled ? (
                      <LoadableAgentReplacement />
                    ) : (
                      <NotFound isNested />
                    )}
                  </React.Fragment>
                )}
              </FadeInContainer>
            </Container>
          )}
        />

        <Route
          path={paths.checklist}
          render={() => (
            <ChecklistRouteProvider>
              <LoadableChecklist />
            </ChecklistRouteProvider>
          )}
        />

        <Route
          render={() => (
            <Container>
              <NotFound isNested />
            </Container>
          )}
        />
      </Switch>
    </React.Fragment>
  );
};

export { ClientDashboardView };
